import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { audienceListsApi, campaignsApi } from "@/api";
import { format } from "date-fns";
import { ArrowLeft, X, Calendar, Send, MousePointer, AlertTriangle, Eye, Copy, ChevronLeft, ChevronRight, Download, DollarSign, ExternalLink, Users } from "lucide-react";

function StatCard({
  label,
  value,
  total,
  color,
  icon: Icon,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}) {
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={color} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
      <div className="mt-2">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${color.replace("text-", "bg-")}`}
            style={{ width: `${Math.min(parseFloat(pct), 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

const sendStatusLabels: Record<string, string> = {
  QUEUED: "送信待機", HELD: "安全待機", DISPATCHING: "送信処理中", SENT: "送信済", BOUNCED: "バウンス",
  COMPLAINED: "苦情", SKIPPED: "スキップ", CANCELLED: "キャンセル",
  REJECTED: "SES拒否",
  UNKNOWN: "成否不明（要確認）",
};
const sendStatusColors: Record<string, string> = {
  QUEUED: "text-slate-500", HELD: "text-blue-600", DISPATCHING: "text-amber-600", SENT: "text-green-600", BOUNCED: "text-red-600",
  COMPLAINED: "text-red-600", SKIPPED: "text-slate-400", CANCELLED: "text-slate-400",
  REJECTED: "text-red-700",
  UNKNOWN: "text-orange-600",
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sendsPage, setSendsPage] = useState(1);
  const [sendsFilter, setSendsFilter] = useState("");

  const { data: campaign, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => campaignsApi.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "SENDING" || status === "PAUSED" ? 5000 : false;
    },
  });

  const { data: sendsData } = useQuery({
    queryKey: ["campaign-sends", id, sendsPage, sendsFilter],
    queryFn: () => {
      const params: Record<string, string> = { page: String(sendsPage), limit: "20" };
      if (sendsFilter) params.status = sendsFilter;
      return campaignsApi.sends(id!, params);
    },
    enabled: !!id && !!campaign && ["SENT", "SENDING", "PAUSED", "CANCELLED"].includes(campaign.status),
  });

  const audienceListId = campaign?.audienceList?.id;
  const { data: audienceList } = useQuery({
    queryKey: ["campaign-audience-list", audienceListId],
    queryFn: () => audienceListsApi.get(audienceListId!),
    enabled: !!audienceListId,
  });

  const { data: audienceMembers } = useQuery({
    queryKey: ["campaign-audience-list-members", audienceListId],
    queryFn: () => audienceListsApi.members(audienceListId!, { status: "INCLUDED", page: "1", limit: "10" }),
    enabled: !!audienceListId,
  });

  const cancelMutation = useMutation({
    mutationFn: () => campaignsApi.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  const { data: conversionsData } = useQuery({
    queryKey: ["campaign-conversions", id],
    queryFn: () => campaignsApi.conversions(id!),
    enabled: !!id && !!campaign && ["SENT", "SENDING", "PAUSED", "CANCELLED"].includes(campaign?.status ?? ""),
  });

  const cloneMutation = useMutation({
    mutationFn: () => campaignsApi.clone(id!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${data.id}/edit`);
    },
  });

  if (isLoading) {
    return <p className="text-slate-500">読み込み中...</p>;
  }

  if (!campaign) {
    return <p className="text-slate-500">キャンペーンが見つかりません</p>;
  }

  const statusColors: Record<string, string> = {
    DRAFT: "bg-slate-100 text-slate-700",
    SCHEDULED: "bg-blue-100 text-blue-700",
    SENDING: "bg-amber-100 text-amber-700",
    PAUSED: "bg-red-100 text-red-700",
    SENT: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };
  const statusLabels: Record<string, string> = {
    DRAFT: "下書き",
    SCHEDULED: "予約済",
    SENDING: "送信中",
    PAUSED: "安全停止",
    SENT: "送信済",
    CANCELLED: "キャンセル",
  };

  const total = campaign.totalSent + campaign.totalBounced;
  const opened = campaign.stats?.opened ?? campaign.totalOpened;
  const clicked = campaign.stats?.clicked ?? campaign.totalClicked;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/")} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold truncate">{campaign.name}</h2>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[campaign.status] || ""}`}>
              {statusLabels[campaign.status] || campaign.status}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">{campaign.subject}</p>
        </div>
        <div className="flex gap-2">
          {campaign.channel === "EMAIL" && campaign.htmlBody && (
            <a
              href={`/api/campaigns/${id}/preview`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
            >
              <Eye size={14} />
              HTMLプレビュー
            </a>
          )}
          {campaign.status === "DRAFT" && (
            <button
              onClick={() => navigate(`/campaigns/${id}/edit`)}
              className="px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
            >
              編集
            </button>
          )}
          <button
            onClick={() => cloneMutation.mutate()}
            disabled={cloneMutation.isPending}
            className="flex items-center gap-1 px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
          >
            <Copy size={14} />
            {cloneMutation.isPending ? "複製中..." : "複製"}
          </button>
        </div>
      </div>

      {/* Meta info */}
      <div className="bg-white p-4 rounded-lg shadow-sm border flex flex-wrap gap-6 text-sm">
        {campaign.sentAt && (
          <div>
            <span className="text-slate-500">送信日時:</span>{" "}
            <span className="font-medium">{format(new Date(campaign.sentAt), "yyyy/MM/dd HH:mm")}</span>
          </div>
        )}
        {campaign.scheduledAt && campaign.status === "SCHEDULED" && (
          <div className="flex items-center gap-1">
            <Calendar size={14} className="text-blue-500" />
            <span className="text-slate-500">予約日時:</span>{" "}
            <span className="font-medium">{format(new Date(campaign.scheduledAt), "yyyy/MM/dd HH:mm")}</span>
          </div>
        )}
        <div>
          <span className="text-slate-500">作成日:</span>{" "}
          <span className="font-medium">{format(new Date(campaign.createdAt), "yyyy/MM/dd HH:mm")}</span>
        </div>
        {campaign.emailTemplate && (
          <div>
            <span className="text-slate-500">原稿:</span>{" "}
            <span className="font-medium">{campaign.emailTemplate.name}</span>
          </div>
        )}
        {campaign.audienceList && (
          <button
            type="button"
            onClick={() => navigate(`/audience-lists/${campaign.audienceList?.id}`)}
            className="text-left"
          >
            <span className="text-slate-500">宛先リスト:</span>{" "}
            <span className="font-medium text-blue-600 hover:text-blue-700">{campaign.audienceList.name}</span>
          </button>
        )}
      </div>

      {/* Stats grid */}
      {(campaign.status === "SENT" || campaign.status === "SENDING" || campaign.status === "PAUSED" || campaign.status === "CANCELLED") && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="送信" value={campaign.totalSent} total={total || 1} color="text-slate-600" icon={Send} />
          <StatCard label="開封" value={opened} total={campaign.totalSent || 1} color="text-green-600" icon={Eye} />
          <StatCard label="クリック" value={clicked} total={campaign.totalSent || 1} color="text-blue-600" icon={MousePointer} />
          <StatCard label="バウンス" value={campaign.totalBounced} total={total || 1} color="text-red-600" icon={AlertTriangle} />
        </div>
      )}

      {/* Status-specific actions */}
      {(campaign.status === "SENDING" || campaign.status === "SCHEDULED" || campaign.status === "PAUSED") && (
        <div className="flex gap-3">
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
          >
            <X size={16} />
            {cancelMutation.isPending ? "キャンセル中..." : campaign.status === "SCHEDULED" ? "予約キャンセル" : "送信キャンセル"}
          </button>
        </div>
      )}

      {campaign.status === "SENDING" && (
        <p className="text-xs text-amber-600 animate-pulse">送信中... 5秒ごとに自動更新</p>
      )}

      {campaign.status === "PAUSED" && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-bold">安全機能により残りの配信を停止しました</p>
          <p className="mt-1">{campaign.emailBatchHaltReason || "バッチ判定で異常を検出しました。"}</p>
        </div>
      )}

      {campaign.emailBatchEnabled && campaign.deliveryBatches?.length ? (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-bold mb-3">自動分割配信（各回の観察 {campaign.emailBatchObservationMinutes}分）</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {campaign.deliveryBatches.map((batch) => (
              <div key={batch.id} className="rounded border p-3 text-xs">
                <p className="font-bold">第{batch.ordinal}回 / {batch.expectedCount.toLocaleString()}件</p>
                <p className="mt-1 text-slate-600">{({ HELD: "安全待機", RELEASED: "送信・観察中", PASSED: "判定通過", HALTED: "異常停止" } as const)[batch.status]}</p>
                {(batch.hardBounceCount > 0 || batch.complaintCount > 0 || batch.rejectedCount > 0) && (
                  <p className="mt-1 text-red-700">恒久バウンス {batch.hardBounceCount} / 苦情 {batch.complaintCount} / 拒否 {batch.rejectedCount}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {campaign.audienceList && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-blue-500" />
              <h3 className="text-sm font-bold">宛先リスト</h3>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/audience-lists/${campaign.audienceList?.id}`)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-white"
            >
              <ExternalLink size={12} />
              リスト管理
            </button>
          </div>
          <div className="p-4 border-b">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-slate-500">リスト:</span>{" "}
                <span className="font-medium">{campaign.audienceList.name}</span>
              </div>
              <div>
                <span className="text-slate-500">対象:</span>{" "}
                <span className="font-medium">
                  {(audienceList?.eligibleCount ?? audienceList?.lastCount ?? campaign.segmentCount)?.toLocaleString() ?? "-"}件
                </span>
              </div>
              <div>
                <span className="text-slate-500">モード:</span>{" "}
                <span className="font-medium">{audienceList?.mode ?? campaign.audienceList.mode}</span>
              </div>
              {audienceList?.updatedAt && (
                <div>
                  <span className="text-slate-500">更新:</span>{" "}
                  <span className="font-medium">{format(new Date(audienceList.updatedAt), "yyyy/MM/dd HH:mm")}</span>
                </div>
              )}
            </div>
          </div>
          {audienceMembers ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">会社名</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">氏名</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">メール</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">区分</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {audienceMembers.data.map((member) => {
                    const contact = member.contact;
                    return (
                      <tr
                        key={member.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => contact?.id && navigate(`/contacts/${contact.id}`)}
                      >
                        <td className="px-4 py-2">{contact?.company || "-"}</td>
                        <td className="px-4 py-2 text-slate-600">
                          {[contact?.lastName, contact?.firstName].filter(Boolean).join(" ") || "-"}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{contact?.email || "-"}</td>
                        <td className="px-4 py-2 text-slate-500">{contact?.traderType || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-4 py-2 border-t text-xs text-slate-500 flex justify-between">
                <span>先頭10件を表示</span>
                <span>{audienceMembers.total.toLocaleString()}件中</span>
              </div>
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-slate-400">宛先を読み込み中...</div>
          )}
        </div>
      )}

      {campaign.channel === "EMAIL" && campaign.htmlBody && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-green-500" />
              <h3 className="text-sm font-bold">HTMLプレビュー</h3>
            </div>
            <a
              href={`/api/campaigns/${id}/preview`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-white"
            >
              <ExternalLink size={12} />
              別タブ
            </a>
          </div>
          <iframe
            src={`/api/campaigns/${id}/preview`}
            title="HTMLプレビュー"
            className="w-full h-[680px] bg-white"
          />
        </div>
      )}

      {/* Segment summary */}
      {campaign.segmentQuery && Object.keys(campaign.segmentQuery).length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-bold mb-2">セグメント条件</h3>
          <div className="flex flex-wrap gap-2">
            {campaign.segmentQuery.traderType?.map((v) => (
              <span key={v} className="px-2 py-0.5 rounded bg-slate-100 text-xs">{v}</span>
            ))}
            {campaign.segmentQuery.traderRank?.map((v) => (
              <span key={`rank-${v}`} className="px-2 py-0.5 rounded bg-amber-50 text-xs">ランク: {v}</span>
            ))}
            {campaign.segmentQuery.source?.map((v) => (
              <span key={v} className="px-2 py-0.5 rounded bg-blue-50 text-xs">{v}</span>
            ))}
            {campaign.segmentQuery.rfmRank?.map((v) => (
              <span key={v} className="px-2 py-0.5 rounded bg-green-50 text-xs">RFM: {v}</span>
            ))}
            {campaign.segmentQuery.leadScoreMin !== undefined && (
              <span className="px-2 py-0.5 rounded bg-orange-50 text-xs">スコア &ge; {campaign.segmentQuery.leadScoreMin}</span>
            )}
            {campaign.segmentQuery.leadScoreMax !== undefined && (
              <span className="px-2 py-0.5 rounded bg-orange-50 text-xs">スコア &le; {campaign.segmentQuery.leadScoreMax}</span>
            )}
            {campaign.segmentQuery.lastOrderWithin !== undefined && (
              <span className="px-2 py-0.5 rounded bg-purple-50 text-xs">注文: {campaign.segmentQuery.lastOrderWithin}日以内</span>
            )}
            {campaign.segmentQuery.hasFaxNumber && (
              <span className="px-2 py-0.5 rounded bg-orange-50 text-xs">FAX番号あり</span>
            )}
          </div>
        </div>
      )}

      {/* Status-by-status breakdown */}
      {campaign.stats?.byStatus && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-bold mb-2">ステータス内訳</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            {Object.entries(campaign.stats.byStatus).map(([status, count]) => (
              <div key={status}>
                <span className="text-slate-500">{status}:</span>{" "}
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sends drill-down table */}
      {sendsData && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold">送信先一覧 ({sendsData.total}件)</h3>
              <a
                href={`/api/campaigns/${id}/sends/export`}
                className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-white"
              >
                <Download size={12} />
                CSV
              </a>
            </div>
            <select
              value={sendsFilter}
              onChange={(e) => { setSendsFilter(e.target.value); setSendsPage(1); }}
              className="px-2 py-1 border rounded text-xs"
            >
              <option value="">全て</option>
              <option value="SENT">送信済</option>
              <option value="BOUNCED">バウンス</option>
              <option value="QUEUED">待機中</option>
              <option value="HELD">安全待機</option>
              <option value="REJECTED">SES拒否</option>
              <option value="SKIPPED">スキップ</option>
            </select>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">メール</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">会社名</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">ステータス</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">送信日</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">開封</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">クリック</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sendsData.data.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/contacts/${s.contactId}`)}
                >
                  <td className="px-4 py-2">{s.contact.email}</td>
                  <td className="px-4 py-2 text-slate-500">{s.contact.company || "-"}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-medium ${sendStatusColors[s.status] || ""}`}>
                      {sendStatusLabels[s.status] || s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs">
                    {s.sentAt ? format(new Date(s.sentAt), "MM/dd HH:mm") : "-"}
                  </td>
                  <td className="px-4 py-2">
                    {s.openedAt ? (
                      <span className="text-green-600 text-xs">{format(new Date(s.openedAt), "MM/dd HH:mm")}</span>
                    ) : "-"}
                  </td>
                  <td className="px-4 py-2">
                    {s.clickedAt ? (
                      <span className="text-blue-600 text-xs">{format(new Date(s.clickedAt), "MM/dd HH:mm")}</span>
                    ) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sendsData.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t">
              <span className="text-xs text-slate-500">
                {(sendsPage - 1) * 20 + 1}-{Math.min(sendsPage * 20, sendsData.total)} / {sendsData.total}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setSendsPage((p) => Math.max(1, p - 1))}
                  disabled={sendsPage <= 1}
                  className="p-1 border rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setSendsPage((p) => Math.min(sendsData.totalPages, p + 1))}
                  disabled={sendsPage >= sendsData.totalPages}
                  className="p-1 border rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conversion attribution */}
      {conversionsData && conversionsData.totalCount > 0 && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-amber-500" />
              <h3 className="text-sm font-bold">帰属コンバージョン</h3>
            </div>
            <div className="text-sm">
              <span className="text-slate-500">{conversionsData.totalCount}件 /</span>{" "}
              <span className="font-bold text-amber-600">{Math.round(conversionsData.totalRevenue).toLocaleString()}円</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">コンタクト</th>
                <th className="text-right px-4 py-2 font-medium text-slate-600">注文額</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">帰属</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">注文日</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {conversionsData.data.slice(0, 20).map((cv) => (
                <tr
                  key={cv.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/contacts/${cv.contactId}`)}
                >
                  <td className="px-4 py-2">
                    <p className="text-xs font-medium">{cv.contact?.company || cv.contact?.email || "-"}</p>
                    {cv.contact?.company && <p className="text-xs text-slate-400">{cv.contact.email}</p>}
                  </td>
                  <td className="px-4 py-2 text-right font-medium text-amber-600">
                    {Math.round(cv.orderAmount).toLocaleString()}円
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{cv.attributionType}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {format(new Date(cv.orderDate), "yyyy/MM/dd")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(cancelMutation.isError || cloneMutation.isError) && (
        <p className="text-sm text-red-600">{(cancelMutation.error || cloneMutation.error)?.message}</p>
      )}
    </div>
  );
}
